import { snakeCase } from 'lodash';
import formatJsonApiData from '@/utils/format-json-api-data';
import { getWithDefaultValues } from '@/utils/models';
import { loadResourceWithPlaceholder } from '@/utils/load-resource';
import { parseServerErrors, isServerError, isApiError } from '@/utils/errors';
import deepMapKeys from '@/utils/deep-map-keys';

// Tracks whether an entity was loaded at least once. Used when force reload is
// specified with either the `reload` query param or the `entityReload` key.
// Force reload is useful when a partial compound document exists in the store
// but we would like to fully load it in another view.
//
// One example of this is loading an item page which gives us partial user data
// for the item seller. Then navigating to the profile page of that user should
// fully load all the data for that user.
const _loadedEntity = {};

// `entity-show-view` defines behavior for a route view that loads a single
// record (analogous to a `show` action in rails).
//
// The mixee must add a data key `entityClass` that holds the model class.
// It can optionally add a data key `entityIdentifierName` to override the
// primary identifier of the record. For example, `token` instead of `id` for
// orders.
// It can optionally add a data key `entityQuery` to send extra query params on
// save/update.
// It can optionally add a `transformEntityData` method to modify data before
// save/update.
// It can optionally add an `entityLoaded` method which acts as a callback that
// fires once the entity is loaded.
// It can optionally add an `entityReload` key which will force the entity to
// load *once* even if data already exists in the store.
// It can optionally add an `errorListRef` key which should return a ref to the
// error list component. It will be scrolled into view if populated with API
// errors.
//
// For example:
// ```
// data: {
//   return {
//     entityClass: Post,
//     entityIdentifierName: 'token',
//     entityQuery: { user_id: this.currentUser.id }
//   }
// },
// methods: {
//   entityLoaded(entity) {
//   },
//   transformEntityData(entityData) {
//     return {
//       ...entityData,
//       location: entityData.location.toLowerCase(),
//     };
//   },
// },
// ```
export default {
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.loadEntity();
    });
  },

  data() {
    return {
      isLoading: false,
      isUpdating: false,
      entityErrors: [],
      entity: {},
      entityIdentifierName: 'id',
      // This is the ID that the backend returns. It takes presedence over
      // whatever came from the URL. This is useful for case insensitive routes.
      entityIdentifierCanonical: null,
      // TODO: Make this a computed prop.
      entityQuery: {},
      entityReload: false,
    };
  },

  computed: {
    isNewEntity() {
      return this.entityIdentifier === 'new';
    },

    entityIdentifier() {
      if (this.entityIdentifierCanonical) return this.entityIdentifierCanonical;

      const id = this.$route.params[this.entityIdentifierName];
      return id ? id.toString() : null;
    },

    entityRecord() {
      if (!this.entityIdentifier) return;

      if (this.isNewEntity) {
        return new this.entityClass();
      }

      return this.entityClass
        .query()
        .where(this.entityIdentifierName, this.entityIdentifier)
        .withAllRecursive()
        .first();
    },

    // Used to look up whether an entity was loaded once. Used with
    // `entityReload`.
    loadedEntityKey() {
      const q = JSON.stringify(this.entityQuery);
      return `${this.entityClass.entity}/${this.entityIdentifier}?${q}`;
    },

    shouldReload() {
      if (!(this.$route.query.reload || this.entityReload)) return false;
      if (!this.entityRecord) return false;

      return !_loadedEntity[this.loadedEntityKey];
    },

    errorListRef() {
      return this.$refs.form?.$refs?.errorList;
    },

    entityName() {
      return this.entityClass.apiEntity || this.entityClass.entity;
    },
  },

  methods: {
    async _loadEntity() {
      this.isLoading = true;

      const query = {
        ...this.entityQuery,
      };

      const vuexAxiosResponse = await loadResourceWithPlaceholder(
        this.$store,
        () =>
          this.entityClass
            .api()
            .get(`/${this.entityName}/${this.entityIdentifier}`, {
              params: query,
            })
      );

      const entity = vuexAxiosResponse?.response.data.data;
      if (entity) {
        this.entityIdentifierCanonical = entity[this.entityIdentifierName];
      }

      this.isLoading = false;

      _loadedEntity[this.loadedEntityKey] = true;

      return entity;
    },

    async loadEntity() {
      if (this.isNewEntity) return;

      // Avoid loading if its already in the store.
      if (this.entityRecord && !this.shouldReload) {
        if (this.entityLoaded) this.entityLoaded(this.entityRecord);
        return this.entityRecord;
      }

      const entity = await this._loadEntity();

      if (this.entityLoaded && entity) {
        this.entityLoaded(entity);
      }
    },

    // TODO: Update this to also call `entityLoaded()` by moving the callback
    // into `this._loadEntity()`.
    async reloadEntity() {
      if (this.isNewEntity) return;

      return await this._loadEntity();
    },

    async deleteEntity({ confirm = false } = {}) {
      if (!this.entityRecord) return;

      // TODO: Replace this with undo functionality later.
      if (confirm) {
        if (!window.confirm('Are you sure you want to delete?')) {
          return;
        }
      }

      const id = this.entityRecord[this.entityIdentifierName];

      await this.entityClass.api().delete(`/${this.entityName}/${id}`, {
        delete: id,
      });

      this.$router.back();
    },

    transformEntityData(entityData) {
      return entityData;
    },

    async createOrUpdateEntity() {
      // TODO: Consider passing in a copy of `this.entity` in case something
      // modifies it on the inside.
      const data = this.transformEntityData(this.entity);
      const params = {};

      if (this.currentUser) {
        params.user_id = this.currentUser.id;
      }

      const options = {
        params,
        data: formatJsonApiData(
          this.entityClass,
          deepMapKeys(data, (value, key) => snakeCase(key))
        ),
      };

      const record = await this.entityRecord;

      if (record.id) {
        const id = record[this.entityIdentifierName];
        options.method = 'put';
        options.url = `/${this.entityName}/${id}`;
      } else {
        options.method = 'post';
        options.url = `/${this.entityName}`;
      }

      let entity;

      this.isUpdating = true;
      this.entityErrors = [];

      try {
        const vuexAxiosResponse = await this.entityClass.api().request(options);
        entity = vuexAxiosResponse.response.data.data;
      } catch (error) {
        this.entityErrors = parseServerErrors(error);

        this.$nextTick().then(() => this.errorListRef?.$el.scrollIntoView());

        // 4xx errors are a normal way to indicate that the user input was
        // invalid. We avoid bubbling those to the terminal otherwise Sentry
        // will create noise in Slack.
        if (!isApiError(error) || isServerError(error)) {
          throw error;
        }
      } finally {
        this.isUpdating = false;
      }

      return entity;
    },
  },

  watch: {
    // When the entity loads and gets populated in the computed prop, update the
    // data values which can be used by a form.
    // NOTE: We store separate values in data so that something like a form can
    // be updated without directly modifying the underlying model until save.
    entityRecord: {
      immediate: true,
      async handler(entityRecord) {
        if (!entityRecord) {
          return;
        }

        // One example where `entityRecord` is a thenable is when editing the
        // current user.
        const record = await entityRecord;
        this.entity = getWithDefaultValues(record);
      },
    },
  },
};
